import {
  CAMPAIGN_OBJECTIVES,
  CAMPAIGN_SPECIAL_DATES,
  type CampaignFilters,
  type CampaignObjectiveKey,
  type CampaignSpecialDateKey,
  campaignDaysBefore,
  campaignFiltersFor,
  campaignUsesDate,
  formatDate,
  specialDateDaysBefore,
  specialDateOf,
  specialDateOption,
} from '@crm/shared';
import { Card, CardContent, cn, Input, Label, RadioGroup, Select, Spinner } from '@crm/ui';
import {
  Cake,
  CalendarHeart,
  HeartHandshake,
  type LucideIcon,
  Repeat,
  Scissors,
  Ticket,
} from 'lucide-react';
import { Autocomplete } from '@/components';
import { useServices } from '@/modules/catalog/api';
import { useUnits } from '@/modules/company/hooks';
import type { CampaignInput } from '../api';

const ICONE: Record<CampaignObjectiveKey, LucideIcon> = {
  COUPON: Ticket,
  WINBACK: HeartHandshake,
  RECURRENCE: Repeat,
  SERVICE: Scissors,
  SPECIAL_DATE: CalendarHeart,
  BIRTHDAY: Cake,
};

interface ObjectiveTabProps {
  /**
   * O que entra depois do objetivo, no mesmo cartao.
   *
   * O cupom e parte da mesma decisao: em cartao separado, a oferta parecia
   * outro assunto - e a tela ganhava uma borda para atravessar no meio de uma
   * escolha so.
   */
  children?: React.ReactNode;
  objective: CampaignObjectiveKey;
  specialDate: CampaignSpecialDateKey | null;
  scheduledAt: string | null;
  filters: CampaignFilters;
  readOnly: boolean;
  onChange: (values: CampaignInput) => void;
}

/** `yyyy-mm-dd`, que e o que o campo de data entende. */
function paraCampo(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '';
}

/**
 * Por que a campanha existe - e a primeira pergunta, e a unica que o assinante
 * precisa saber responder.
 *
 * Cada objetivo carrega as proprias opcoes dentro do cartao, em vez de
 * espalha-las por outros passos. Quem escolhe "recuperar clientes antigos"
 * quer dizer, na mesma frase, de quantos dias - separar as duas coisas obriga
 * a decidir metade agora e metade depois.
 */
export function ObjectiveTab({
  children,
  objective,
  specialDate,
  scheduledAt,
  filters,
  readOnly,
  onChange,
}: ObjectiveTabProps) {
  const units = useUnits();
  const _unidades = units.data?.data ?? [];

  const services = useServices({ page: 1, perPage: 100 }, { enabled: objective === 'SERVICE' });

  const _escolhido = CAMPAIGN_OBJECTIVES.find((item) => item.key === objective);

  /**
   * Trocar de objetivo limpa o que era do anterior.
   *
   * Sem isso, um servico escolhido continuaria gravado depois de virar
   * campanha de aniversario - invisivel na tela, mas contando na hora de
   * montar o publico.
   */
  function trocaObjetivo(proximo: CampaignObjectiveKey) {
    onChange({
      objective: proximo,
      filters: campaignFiltersFor(proximo, filters),
      specialDate: proximo === 'SPECIAL_DATE' ? specialDate : null,
      scheduledAt: campaignUsesDate(proximo) ? scheduledAt : null,
    });
  }

  /** O proximo feriado desta data, deste ano ou do ano que vem. */
  function proximoFeriado(chave: CampaignSpecialDateKey): Date | null {
    const hoje = new Date();
    const desteAno = specialDateOf(chave, hoje.getFullYear());

    return desteAno && desteAno >= hoje ? desteAno : specialDateOf(chave, hoje.getFullYear() + 1);
  }

  function disparoDe(chave: CampaignSpecialDateKey, dias: number): string | null {
    const feriado = proximoFeriado(chave);
    if (!feriado) return null;

    const saida = new Date(feriado.getFullYear(), feriado.getMonth(), feriado.getDate() - dias, 9);

    return saida.toISOString();
  }

  /** Data comemorativa conhecida ja traz o proprio dia - ninguem procura no calendario. */
  function trocaData(chave: CampaignSpecialDateKey) {
    const dias = specialDateDaysBefore(chave);

    onChange({
      specialDate: chave,
      filters: { ...filters, daysBefore: dias },
      scheduledAt: disparoDe(chave, dias),
    });
  }

  /**
   * A antecedencia e a unica coisa escolhida: o dia do disparo sai dela, e nao
   * o contrario. Guardar os dois evita que o worker precise refazer a conta.
   */
  function trocaAntecedencia(valor: string) {
    const dias = Number(valor);
    const limpo = valor && Number.isFinite(dias) && dias >= 0 ? dias : null;

    onChange({
      filters: { ...filters, daysBefore: limpo },
      ...(objective === 'SPECIAL_DATE' && specialDate && specialDate !== 'OTHER'
        ? { scheduledAt: disparoDe(specialDate, limpo ?? 0) }
        : {}),
    });
  }

  function trocaDias(campo: 'inactiveDays' | 'sinceLastVisitDays', valor: string) {
    const numero = Number(valor);

    onChange({
      filters: {
        ...filters,
        [campo]: valor && Number.isFinite(numero) && numero > 0 ? numero : null,
      },
    });
  }

  const rotuloDeDias =
    objective === 'RECURRENCE'
      ? 'A partir de quantos dias do ultimo atendimento?'
      : 'A partir de quantos dias sem retorno?';
  const campoDeDias = objective === 'RECURRENCE' ? 'sinceLastVisitDays' : 'inactiveDays';

  // Aniversario sempre; data especial so quando o feriado tem dia conhecido.
  const temAntecedencia =
    objective === 'BIRTHDAY' ||
    (objective === 'SPECIAL_DATE' && !!specialDate && specialDate !== 'OTHER');

  const dataDoDisparo =
    objective === 'SPECIAL_DATE' && scheduledAt ? formatDate(scheduledAt) : null;

  return (
    <div className="space-y-4">
      <Card variant="flush">
        <CardContent className="space-y-6 py-6">
          <Label>Objetivo da campanha</Label>

          {/* Um cartao por objetivo, e as opcoes dele abrindo por baixo. Um
              select economizaria espaco, mas esconderia justamente o que o
              assinante precisa comparar antes de decidir. */}
          <div className="grid gap-2 sm:grid-cols-2">
            {CAMPAIGN_OBJECTIVES.map((item) => {
              const Icone = ICONE[item.key];
              const ativo = item.key === objective;

              return (
                <button
                  key={item.key}
                  type="button"
                  disabled={readOnly}
                  onClick={() => trocaObjetivo(item.key)}
                  className={cn(
                    'flex items-start gap-3 p-3 text-left shadow-sm transition-colors',
                    'border-2',
                    ativo
                      ? 'border-primary bg-primary/10'
                      : 'border-muted bg-muted hover:border-border-strong',
                    readOnly && 'pointer-events-none opacity-60',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                      ativo ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    <Icone className="h-5 w-5" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{item.label}</span>
                    <span className="block text-sm text-muted-foreground">{item.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {objective === 'WINBACK' || objective === 'RECURRENCE' ? (
            <div className="space-y-5 pt-3">
              {/* Duas escolhas excludentes, e nao uma caixa marcada: um numero
                  fixo trata quem vem toda semana como quem vem duas vezes por
                  ano. Escolhida a primeira, o campo de dias sai de cena -
                  ficaria na tela dizendo um numero que ninguem usa. */}
              <div className="space-y-2">
                <Label>Frequencia do disparo</Label>
                <RadioGroup
                  inline
                  label="Frequencia do disparo"
                  disabled={readOnly}
                  options={[
                    {
                      value: 'auto',
                      label: 'O sistema escolhe com base no comportamento do cliente',
                    },
                    { value: 'fixo', label: 'Eu escolho' },
                  ]}
                  value={filters.autoDays ? 'auto' : 'fixo'}
                  onChange={(modo) =>
                    onChange({ filters: { ...filters, autoDays: modo === 'auto' } })
                  }
                />
              </div>

              {filters.autoDays ? null : (
                <div className="space-y-2">
                  <Label htmlFor="campanha-dias">{rotuloDeDias}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="campanha-dias"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      className="w-24"
                      disabled={readOnly}
                      value={filters[campoDeDias] ?? ''}
                      onChange={(event) => trocaDias(campoDeDias, event.target.value)}
                    />
                    <span className="text-sm text-muted-foreground">dias</span>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {objective === 'SERVICE' ? (
            services.isPending ? (
              <Spinner />
            ) : (
              <Autocomplete
                label="Servico promovido"
                placeholder="Buscar servico..."
                emptyLabel="Escolha o servico"
                disabled={readOnly}
                options={(services.data?.data ?? []).map((service) => ({
                  id: service.id,
                  label: service.name,
                }))}
                value={
                  filters.serviceId
                    ? [
                        {
                          id: filters.serviceId,
                          label:
                            (services.data?.data ?? []).find(
                              (service) => service.id === filters.serviceId,
                            )?.name ?? filters.serviceId,
                        },
                      ]
                    : []
                }
                onChange={(opcoes) =>
                  onChange({ filters: { ...filters, serviceId: opcoes[0]?.id ?? null } })
                }
              />
            )
          ) : null}

          {objective === 'SPECIAL_DATE' ? (
            <div className="space-y-2">
              <Label htmlFor="campanha-data-especial">Qual data</Label>
              <Select
                id="campanha-data-especial"
                className="sm:w-72"
                disabled={readOnly}
                value={specialDate ?? ''}
                onChange={(event) => trocaData(event.target.value as CampaignSpecialDateKey)}
              >
                <option value="" disabled>
                  Escolha a data
                </option>
                {CAMPAIGN_SPECIAL_DATES.map((item) => (
                  <option key={item.key} value={item.key}>
                    {specialDateOption(item.key, new Date().getFullYear())}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}

          {/* Datas conhecidas ja trazem o proprio dia, entao o que sobra a
              escolher e a antecedencia - e nao um dia de calendario que a
              pessoa teria de calcular na mao. O mesmo vale para o
              aniversario, que muda de cliente para cliente. */}
          {temAntecedencia ? (
            <div className="space-y-2">
              <Label htmlFor="campanha-antecedencia">Disparar quantos dias antes?</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="campanha-antecedencia"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={365}
                  className="w-24"
                  disabled={readOnly}
                  value={campaignDaysBefore(objective, filters.daysBefore) ?? 0}
                  onChange={(event) => trocaAntecedencia(event.target.value)}
                />
                <span className="text-sm text-muted-foreground">
                  {objective === 'BIRTHDAY' ? 'dias antes do aniversario' : 'dias antes'}
                </span>
              </div>
              {dataDoDisparo ? (
                <p className="text-xs text-muted-foreground">Dispara em {dataDoDisparo}.</p>
              ) : null}
            </div>
          ) : null}

          {/* "Outra data especial" nao tem dia guardado: aqui a data e digitada. */}
          {objective === 'SPECIAL_DATE' && specialDate === 'OTHER' ? (
            <div className="space-y-2">
              <Label htmlFor="campanha-disparo">Dia do disparo</Label>
              <Input
                id="campanha-disparo"
                type="date"
                className="sm:w-56"
                disabled={readOnly}
                value={paraCampo(scheduledAt)}
                onChange={(event) =>
                  onChange({
                    scheduledAt: event.target.value
                      ? new Date(`${event.target.value}T09:00:00`).toISOString()
                      : null,
                  })
                }
              />
            </div>
          ) : null}
          {children ? (
            <div className="-mx-4 border-t border-border px-4 pt-6 sm:-mx-6 sm:px-6">
              {children}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
